import axios from 'axios'
import { ElMessage } from 'element-plus'
import {
  clearStoredSid,
  claimGuestSession,
  getStoredSid,
  syncPendingGuestAnswersBeforeClaim,
  clearPendingGuestAnswers
} from '@/utils/guestExam'
import { clearExamTiming, resolveElapsedSeconds } from '@/utils/examTiming'
import { examDetailPath } from '@/utils/examPricing'
import { isLoggedIn } from '@/utils/loginFlow'

const GUEST_CLAIM_KEY = 'guest_claim_pending'

/** 微信扫码等会离开当前页，需持久化 sid */
export function stashGuestClaimContext({ sid, examId }) {
  if (sid && examId) {
    localStorage.setItem(GUEST_CLAIM_KEY, JSON.stringify({ sid, examId: String(examId) }))
  }
}

export function readGuestClaimContext() {
  const raw = localStorage.getItem(GUEST_CLAIM_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function clearGuestClaimContext() {
  localStorage.removeItem(GUEST_CLAIM_KEY)
}

export function hasPendingGuestClaim(route) {
  return !!resolveClaimParams(route)?.sid
}

function resolveClaimParams(route) {
  const ctx = readGuestClaimContext()
  let examId = route?.query?.examId || ctx?.examId || ''
  let sid = route?.query?.sid || ctx?.sid || ''
  if (!sid && examId) {
    sid = getStoredSid(examId) || ''
  }
  if (!sid) {
    return null
  }
  if (!examId && ctx?.examId) {
    examId = ctx.examId
  }
  return { sid, examId }
}

export function isNotPurchasedClaimError(err) {
  return err?.code === 'not_purchased' || (err?.message || '').includes('未购买')
}

export function isInsufficientBalanceError(err) {
  const msg = err?.message || ''
  return err?.code === 'insufficient_balance' || msg.includes('不足') || msg.includes('金币不足')
}

/** 从 localStorage 读取待认领的测评 examId（充值回跳等场景） */
export function getPendingGuestExamId() {
  return readGuestClaimContext()?.examId || ''
}

/**
 * 付费测评认领失败：引导充值（保留 sid 以便购买后再次认领）
 */
export function redirectForPaidExamClaim(router, examId) {
  ElMessage.warning('该测评需付费查看报告，请充值；到账后将自动扣费并生成报告')
  if (examId) {
    router.push({ path: '/recharge', query: { from: 'exam', examId: String(examId) } })
  } else {
    router.push('/recharge')
  }
}

/** 使用余额购买测评（已购则接口直接成功） */
export async function buyExamById(examId) {
  const token = localStorage.getItem('jwt_token')
  if (!token) {
    throw new Error('请先登录')
  }
  const id = Number(examId)
  if (!id) {
    throw new Error('无效的测评 ID')
  }
  const resp = await axios.post(
    '/api/v1/exam/buy',
    { id },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  )
  if (resp.data.code !== 200) {
    const message = resp.data.message || '购买失败'
    const err = new Error(message)
    if (message.includes('不足')) {
      err.code = 'insufficient_balance'
    }
    throw err
  }
  return true
}

/**
 * 余额充足时：自动扣费购买测评并认领匿名答题生成报告。
 * 用于充值成功回调，或进入充值页时余额已够的场景。
 * @returns {number|null} resultId
 */
export async function completePendingPaidExamAfterRecharge(router, examId) {
  if (!examId) return null
  const ctx = readGuestClaimContext()
  const resolvedExamId = String(ctx?.examId || examId)
  const sid = getStoredSid(resolvedExamId) || ctx?.sid
  if (!sid || !localStorage.getItem('jwt_token')) return null

  await buyExamById(resolvedExamId)
  return tryClaimPendingGuestExam(router, resolvedExamId)
}

/**
 * After login/register, claim anonymous exam session if sid is present.
 * @returns {number|null} resultId to navigate to, or null if no claim happened
 */
export async function claimExamAfterLogin(route) {
  const params = resolveClaimParams(route)
  if (!params?.sid) return null

  let { sid, examId } = params
  if (examId) {
    sid = (await syncPendingGuestAnswersBeforeClaim(examId, sid)) || getStoredSid(examId) || sid
  }
  const totalTime = examId ? resolveElapsedSeconds(examId) : 0
  const data = await claimGuestSession(sid, totalTime)
  if (examId) {
    clearStoredSid(examId)
    clearPendingGuestAnswers(examId)
    clearExamTiming(examId)
  }
  clearGuestClaimContext()
  return data?.resultId || null
}

/**
 * 登录/注册/扫码登录成功后：免费测评直接出报告；付费未购则跳转充值。
 * @returns {'claimed'|'none'|'recharge'}
 */
export async function handleGuestClaimAfterAuth(router, route) {
  const params = resolveClaimParams(route)
  if (!params?.sid) return 'none'

  try {
    const resultId = await claimExamAfterLogin(route)
    if (resultId) {
      ElMessage.success('报告已生成')
      if (isLoggedIn()) {
        router.replace(`/exam/result/${resultId}`)
      } else {
        router.replace({ path: '/login', query: { href: `/exam/result/${resultId}` } })
      }
      return 'claimed'
    }
    return 'none'
  } catch (err) {
    if (isNotPurchasedClaimError(err)) {
      redirectForPaidExamClaim(router, params.examId)
      return 'recharge'
    }
    if (err?.code === 'sid_expired') {
      const e = new Error('答题会话已过期，请返回测评页重新提交后再登录')
      e.code = 'sid_expired'
      throw e
    }
    throw err
  }
}

/** 已购买且存在匿名 sid 时尝试生成报告（充值/购买后） */
export async function tryClaimPendingGuestExam(router, examId) {
  const sid = getStoredSid(examId) || readGuestClaimContext()?.sid
  if (!sid || !localStorage.getItem('jwt_token')) return null

  try {
    const totalTime = resolveElapsedSeconds(examId)
    const data = await claimGuestSession(sid, totalTime)
    clearStoredSid(examId)
    clearPendingGuestAnswers(examId)
    clearExamTiming(examId)
    clearGuestClaimContext()
    ElMessage.success('报告已生成')
    router.replace(`/exam/result/${data.resultId}`)
    return data.resultId
  } catch (err) {
    if (isNotPurchasedClaimError(err)) {
      return null
    }
    throw err
  }
}

export async function claimExamWithSid(sid, examId) {
  const totalTime = examId ? resolveElapsedSeconds(examId) : 0
  const data = await claimGuestSession(sid, totalTime)
  if (examId) {
    clearStoredSid(examId)
    clearExamTiming(examId)
  }
  clearGuestClaimContext()
  return data
}

/** 认领失败且需付费时，跳转测评详情页（已登录提交场景备用） */
export function redirectToExamForPurchase(router, exam) {
  ElMessage.warning('请先购买该测评后查看报告')
  router.push(examDetailPath(exam))
}
